import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';
import getCurrentUser from "../components/common/GetCurrentUser";
import { useNavigate } from 'react-router-dom';
import './Footer.css';

const DOMPURIFY_CONFIG = {
  ADD_TAGS: [
    'style',
    'svg',
    'path',
    'circle',
    'rect',
    'line',
    'polyline',
    'polygon',
    'g',
    'defs'
  ],
  ADD_ATTR: [
    'style',
    'class',
    'id',
    'data-company-name',
    'data-company-name-title',
    'data-current-year',
    'data-scroll-to',
    'fill',
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'viewBox',
    'd',
    'points',
    'x1',
    'x2',
    'y1',
    'y2',
    'x',
    'y',
    'rx',
    'ry',
    'cx',
    'cy',
    'width',
    'height'
  ],
  FORBID_TAGS: ['script']
};

const SHADOW_WRAPPER_CLASS = 'dynamic-footer-shadow-root';

const escapeHtml = (value = '') =>
  value
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const hoistHeadStyles = (doc) => {
  if (!doc.head) return;
  const headStyles = Array.from(doc.head.querySelectorAll('style'));
  if (!headStyles.length) return;

  headStyles.reverse().forEach((styleNode) => {
    const clone = styleNode.cloneNode(true);
    doc.body.insertBefore(clone, doc.body.firstChild);
  });
};

const adaptStylesForShadow = (doc) => {
  doc.querySelectorAll('style').forEach((styleTag) => {
    const content = styleTag.textContent;
    if (!content) return;
    styleTag.textContent = content
      .replace(/:root/g, ':host')
      .replace(/\bhtml\b/g, `.${SHADOW_WRAPPER_CLASS}`)
      .replace(/\bbody\b/g, `.${SHADOW_WRAPPER_CLASS}`);
  });
};

const wrapContentForShadow = (doc) => {
  if (!doc?.body) return;
  const wrapper = doc.createElement('div');
  wrapper.className = SHADOW_WRAPPER_CLASS;
  wrapper.innerHTML = doc.body.innerHTML;
  doc.body.innerHTML = '';
  doc.body.appendChild(wrapper);
};

const replaceFallbackName = (doc, companyName) => {
  if (!doc || !companyName) return;
  const FALLBACK = 'Looks Salon';
  if (FALLBACK.toLowerCase() === companyName.toLowerCase()) return;

  doc.querySelectorAll('.logo-text, [data-company-name]').forEach((node) => {
    if (node.textContent?.trim() === FALLBACK) {
      node.textContent = companyName;
    }
  });

  doc.querySelectorAll('*').forEach((node) => {
    if (node.childNodes?.length) {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent?.includes(FALLBACK)) {
          child.textContent = child.textContent.replace(new RegExp(FALLBACK, 'g'), companyName);
        }
      });
    }
  });
};

const applyCompanyName = (doc, companyName, sectionLabel = 'Footer') => {
  if (!doc || !companyName) return;

  doc.querySelectorAll('[data-company-name]').forEach((node) => {
    node.textContent = companyName;
  });

  const titleNode = doc.querySelector('[data-company-name-title]');
  if (titleNode) {
    titleNode.textContent = `${companyName} - ${sectionLabel}`;
  }

  replaceFallbackName(doc, companyName);
};

const applyCompanyLogo = (doc, logoUrl) => {
  if (!doc || !logoUrl) return;

  // Replace logo images in various possible locations
  // 1. Images inside .footer-address or logo containers
  doc.querySelectorAll('.footer-address img, .logo img').forEach((img) => {
    img.src = logoUrl;
    img.setAttribute('src', logoUrl);
  });

  // 2. Images with logo-related src attributes (bill.jpg, logo, assets/images)
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    // Replace if it's a logo-related path
    if (src.includes('assets/images/bill') || 
        src.includes('bill.jpg') || 
        src.includes('/bill') ||
        (src.includes('assets/images') && (src.includes('logo') || src.includes('bill')))) {
      img.src = logoUrl;
      img.setAttribute('src', logoUrl);
    }
  });

  // 3. First image in footer/footer-address (likely the logo)
  const footerLogo = doc.querySelector('.footer img, .footer-address img');
  if (footerLogo) {
    const src = footerLogo.getAttribute('src') || '';
    // Replace if it looks like a logo path or is in a logo container
    if (src.includes('bill') || src.includes('logo') || src.includes('assets/images') || 
        footerLogo.closest('.footer-address') || !src) {
      footerLogo.src = logoUrl;
      footerLogo.setAttribute('src', logoUrl);
    }
  }
};

const markCurrentYearPlaceholders = (doc) => {
  doc.querySelectorAll('script').forEach((scriptNode) => {
    const content = scriptNode.textContent || '';
    if (/new Date\(\)\.getFullYear/.test(content)) {
      const span = doc.createElement('span');
      span.setAttribute('data-current-year', '');
      scriptNode.replaceWith(span);
    } else {
      scriptNode.remove();
    }
  });
};

const normalizeBookingButtons = (doc) => {
  doc
    .querySelectorAll('[onclick*="scrollToBooking"]')
    .forEach((node) => {
      node.removeAttribute('onclick');
      node.setAttribute('data-scroll-to', 'booking');
    });
};

const prepareFooterDocument = (doc) => {
  hoistHeadStyles(doc);
  wrapContentForShadow(doc);
  adaptStylesForShadow(doc);
  markCurrentYearPlaceholders(doc);
  normalizeBookingButtons(doc);
};

const markServicesListPlaceholder = (doc) => {
  if (!doc) return;
  const serviceHeading = Array.from(
    doc.querySelectorAll('.footer-section h3, .footer-section .footer-title')
  ).find((node) => node.textContent?.trim().toLowerCase().includes('our services'));
  if (!serviceHeading) return;

  let sibling = serviceHeading.nextElementSibling;
  while (sibling && sibling.tagName !== 'UL') {
    sibling = sibling.nextElementSibling;
  }
  if (sibling) {
    sibling.setAttribute('data-footer-services-list', 'true');
  }
};

const Footer = ({ companyId, token }) => {
  const [footerHtml, setFooterHtml] = useState('');
  const [phone, setPhone] = useState('N/A');
  const [email, setEmail] = useState('N/A');
  const { currentUser } = getCurrentUser();
  const [categories, setCategories] = useState([]);
  const shadowHostRef = useRef(null);
  const shadowRootRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFooterContent = async () => {
      try {
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info' }
        );

        const content = response.data.data.public_site_info.footer_content?.body;
        setPhone(response.data.data.phone || 'N/A');
        setEmail(response.data.data.email || 'N/A');
        const companyInfo = response.data.data.company_info;
        const companyName = companyInfo?.name?.trim();
        const logoUrl = companyInfo?.logo_url;
        if (companyInfo) {
          window.__PUBLIC_SITE_COMPANY__ = companyInfo;
        }

        if (content) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, 'text/html');
          applyCompanyName(doc, companyName, 'Footer');
          
          // Apply company logo dynamically
          if (logoUrl) {
            applyCompanyLogo(doc, logoUrl);
          }
          
          prepareFooterDocument(doc);
          markServicesListPlaceholder(doc);
          setFooterHtml(doc.body.innerHTML);
        } else {
          setFooterHtml('');
        }
      } catch (error) {
        console.error('Error fetching footer content:', error);
      }
    };

    fetchFooterContent();
    fetchCategories();
  }, [currentUser]);

  const fetchCategories = async () => {
    try {
      const response = await sendCachedRequest(
        'GET',
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        { cacheKey: 'menu_categories' }
      );
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const ensureShadowRoot = useCallback(() => {
    if (shadowRootRef.current) return shadowRootRef.current;
    if (!shadowHostRef.current) return null;
    shadowRootRef.current = shadowHostRef.current.attachShadow({ mode: 'open' });
    return shadowRootRef.current;
  }, []);

  const sanitizedFooterHtml = useMemo(() => {
    if (!footerHtml) return '';

    const withContactInfo = footerHtml
      .replace(
        /<span id="company-phone"><\/span>/g,
        `<span id="company-phone">${phone}</span>`
      )
      .replace(
        /<span id="company-email"><\/span>/g,
        `<span id="company-email">${email}</span>`
      );

    return DOMPurify.sanitize(withContactInfo, DOMPURIFY_CONFIG);
  }, [footerHtml, phone, email]);

  useEffect(() => {
    const shadowRoot = ensureShadowRoot();
    if (!shadowRoot) return;

    if (!sanitizedFooterHtml) {
      shadowRoot.innerHTML = '';
      return;
    }

    // Inject CSS to ensure footer is not fixed - only positioning, no design changes
    const antiFixedStyles = `
      <style>
        :host {
          position: relative !important;
        }
        .dynamic-footer-shadow-root {
          position: relative !important;
        }
        .footer, [class*="footer"], [id*="footer"] {
          position: relative !important;
          bottom: auto !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
        }
        /* Footer logo size control */
        .footer-address img,
        .footer img,
        .footer .logo img,
        img[src*="bill"],
        img[src*="logo"] {
          max-width: 150px !important;
          max-height: 80px !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain;
        }
      </style>
    `;

    shadowRoot.innerHTML = antiFixedStyles + sanitizedFooterHtml;

    const yearValue = new Date().getFullYear().toString();
    shadowRoot.querySelectorAll('[data-current-year]').forEach((node) => {
      node.textContent = yearValue;
    });

    const handleScroll = (event) => {
      event.preventDefault();
      const booking = document.getElementById('booking');
      if (booking) {
        booking.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = '/#booking';
      }
    };

    const clickableNodes = Array.from(
      shadowRoot.querySelectorAll('[data-scroll-to="booking"]')
    );

    clickableNodes.forEach((node) => {
      node.addEventListener('click', handleScroll);
    });

    // Handle link clicks in footer HTML to use React Router navigation
    const handleLinkClick = (event) => {
      const anchor = event.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Check if it's an external link or special link
      const isExternal = /^(https?:|mailto:|tel:|\/\/)/.test(href);
      const isAnchor = href.startsWith('#');
      const isMailto = href.startsWith('mailto:');
      const isTel = href.startsWith('tel:');

      // Allow default behavior for external links, anchors, mailto, and tel
      if (isExternal || isAnchor || isMailto || isTel) {
        return; // Allow default behavior
      }

      // Internal link - prevent default and use React Router
      event.preventDefault();
      event.stopPropagation();

      // Normalize the path (ensure it starts with /)
      const path = href.startsWith('/') ? href : `/${href}`;
      
      // Navigate using React Router (navigate is stable, doesn't need to be in deps)
      navigate(path);
    };

    shadowRoot.addEventListener('click', handleLinkClick);

    return () => {
      clickableNodes.forEach((node) => {
        node.removeEventListener('click', handleScroll);
      });
      shadowRoot.removeEventListener('click', handleLinkClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedFooterHtml, ensureShadowRoot]);

  useEffect(() => {
    if (!shadowRootRef.current || !sanitizedFooterHtml) return;
    const targetList =
      shadowRootRef.current.querySelector('[data-footer-services-list]') ||
      shadowRootRef.current.getElementById('shop-categories-placeholder');
    if (!targetList) return;

    if (!categories.length) {
      targetList.innerHTML = `
        <li style="color: var(--gray-300, #D1D5DB); opacity: 0.9;">
          Services will be updated here soon.
        </li>
      `;
      return;
    }

    targetList.innerHTML = categories
      .slice(0, 6)
      .map((cat) => {
        const name = escapeHtml(cat?.name || 'Category');
        const linkTarget = cat?.id
          ? `/services?category_id=${encodeURIComponent(cat.id)}`
          : `/services?category=${encodeURIComponent(cat?.name || '')}`;
        return `
          <li>
            <a href="${linkTarget}">
              ${name}
            </a>
          </li>
        `;
      })
      .join('');
  }, [sanitizedFooterHtml, categories]);

  return (
    <>
      <div
        className="dynamic-footer"
        ref={shadowHostRef}
      />
    </>
  );
};

export default Footer;